import styled, { css } from 'styled-components';

import { breakpoints, colors, fonts, layout, shadows, weights } from './tokens';

export const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(
    to bottom,
    ${colors.pageGradientFrom} 0%,
    ${colors.pageGradientTo} 100%
  );
  font-family: ${fonts.baloo};
  color: ${colors.foreground};
`;

export const Main = styled.main`
  padding-top: ${layout.headerHeight};
`;

export const Container = styled.div`
  max-width: ${layout.maxWidth};
  margin: 0 auto;
`;

export const HeaderBar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: ${colors.card};
  box-shadow: ${shadows.header};
`;

export const HeaderInner = styled(Container)`
  height: ${layout.headerHeight};
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const HeaderLogo = styled.img`
  height: 64px;
  width: auto;
  object-fit: contain;
`;

const ctaButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 28px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  font-family: ${fonts.raleway};
  font-weight: ${weights.extrabold};
  font-size: 15px;
  background: ${colors.primary};
  color: ${colors.white};
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.accent};
    color: ${colors.foreground};
  }
`;

export const HeaderCta = styled.a`
  ${ctaButton};

  @media (max-width: ${breakpoints.md}) {
    display: none;
  }
`;

export const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${colors.primary};

  @media (max-width: ${breakpoints.md}) {
    display: flex;
    align-items: center;
  }
`;

export const MobileNav = styled.nav`
  display: none;

  @media (max-width: ${breakpoints.md}) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 24px;
    background: ${colors.card};
    border-top: 1px solid ${colors.border};
  }
`;

export const MobileCta = styled.a`
  ${ctaButton};
  width: 100%;
`;

export const HeroSection = styled.section`
  max-width: ${layout.maxWidth};
  margin: 60px auto 40px;
  padding: 0 30px;
  text-align: center;
`;

export const HeroTitle = styled.h1`
  margin: 0 0 16px;
  display: inline-block;
  font-family: ${fonts.raleway};
  font-weight: ${weights.black};
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.2;
  letter-spacing: 0;
  color: ${colors.foreground};
`;

export const HeroSubtitle = styled.p`
  max-width: 700px;
  margin: 0 auto 64px;
  font-family: ${fonts.baloo};
  font-weight: ${weights.normal};
  font-size: clamp(16px, 2.5vw, 20px);
  line-height: 1.6;
  color: ${colors.foreground}cc;
`;


export const ServicesSection = styled.section`
  margin: -30px 0 80px;
  padding: 20px 24px;
`;

export const ServicesGrid = styled.div`
  max-width: ${layout.maxWidth};
  margin: 0 auto;
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(${layout.cardMinWidth}, 1fr));
  justify-content: center;

  @media (min-width: ${breakpoints.lg}) {
    grid-template-columns: repeat(
      auto-fit,
      minmax(${layout.cardMinWidth}, ${layout.cardMaxWidth})
    );
  }
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 400px;
  padding: 32px;
  background: ${colors.card};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: ${shadows.card};
  transition: all 0.4s ease;

  &:hover {
    border-color: ${colors.accent};
    box-shadow: ${shadows.cardHover};
    transform: translateY(-4px);
  }
`;

export const CardIcon = styled.div<{ $gradient: string }>`
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${shadows.icon};
  background: ${({ $gradient }) => $gradient};
`;

export const CardLabel = styled.div`
  margin-bottom: 8px;
  font-family: ${fonts.raleway};
  font-weight: ${weights.bold};
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 1.5;
  color: ${colors.primaryDark};
`;

export const CardTitle = styled.h3`
  margin: 0 0 16px;
  font-family: ${fonts.raleway};
  font-weight: ${weights.black};
  font-size: 22px;
  line-height: 1.3;
  color: ${colors.foreground};
`;

export const CardText = styled.p`
  flex-grow: 1;
  min-height: 60px;
  margin: 0 0 32px;
  font-family: ${fonts.baloo};
  font-weight: ${weights.normal};
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.foreground}b3;
`;

export const CardFooter = styled.div`
  margin-top: auto;
`;

export const CardButton = styled.button<{
  $bg: string;
  $color: string;
  $hoverBg: string;
  $hoverColor: string;
}>`
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: ${fonts.raleway};
  font-weight: ${weights.bold};
  font-size: 15px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  transition: all 0.3s ease;

  &:hover {
    background: ${({ $hoverBg }) => $hoverBg};
    color: ${({ $hoverColor }) => $hoverColor};
  }
`;

export const FooterBar = styled.footer`
  margin-top: 20px;
  background: ${colors.foreground};
  color: ${colors.white};
`;

export const FooterInner = styled(Container)`
  padding: 40px 30px;
`;

export const FooterLogo = styled.img`
  height: 48px;
  width: auto;
  margin-bottom: 30px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

export const FooterColumns = styled.div`
  display: grid;
  gap: 48px;
  margin-bottom: 40px;
  grid-template-columns: 1fr;

  @media (min-width: ${breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const FooterTitle = styled.h3`
  margin: 0 0 16px;
  font-family: ${fonts.raleway};
  font-weight: ${weights.extrabold};
  font-size: 18px;
  color: ${colors.white};
`;

export const FooterText = styled.p`
  margin: 0;
  font-family: ${fonts.baloo};
  font-weight: ${weights.normal};
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.7);
`;

export const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FooterList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FooterLink = styled.a`
  position: relative;
  display: inline-block;
  text-decoration: none;
  font-family: ${fonts.baloo};
  font-weight: ${weights.normal};
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${colors.accent};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${colors.accent};
  }

  &:hover::after {
    width: 100%;
  }
`;

export const FooterInlineLink = styled.a`
  text-decoration: underline;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.accent};
  }
`;

export const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`;

export const FooterLegal = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  align-items: center;
  justify-content: center;
`;

export const FooterCopyright = styled.p`
  margin: 0;
  font-family: ${fonts.baloo};
  font-weight: ${weights.normal};
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
`;

export const ModalContent = styled.div`
  padding: 32px;
  font-family: ${fonts.baloo};
  text-align: center;
`;

export const ModalTitle = styled.h3`
  margin: 0 0 8px;
  font-family: ${fonts.raleway};
  font-weight: ${weights.black};
  font-size: 22px;
  line-height: 1.3;
  color: ${colors.foreground};
`;

export const ModalText = styled.p`
  margin: 0 0 24px;
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.foreground}b3;
`;

export const ModalField = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 16px;
  margin-bottom: 16px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  font-family: ${fonts.baloo};
  font-size: 16px;
  color: ${colors.foreground};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.accent};
    box-shadow: 0 0 0 3px ${colors.accent}33;
  }
`;

export const ModalSubmit = styled.button`
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: ${fonts.raleway};
  font-weight: ${weights.bold};
  font-size: 15px;
  background: ${colors.primary};
  color: ${colors.white};
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: ${colors.primaryDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ModalMessage = styled.p`
  margin: 16px 0 0;
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.primaryDark};
`;
